import { useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Check, 
  Instagram, 
  Youtube, 
  Music, 
  Coffee, 
  ExternalLink,
  Trophy,
  Share2,
  Heart,
  Copy,
  Users,
  Gift
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/afrofanthem/",
  youtube: "https://www.youtube.com/@AfroFanthem",
  spotify: "https://open.spotify.com/artist/2p4fVYvsZF323pRXdhUEDC?si=xTM6GHEtTcqNUrPqa7scPA",
  tiktok: "https://www.tiktok.com/@afrofanthem",
  x: "https://x.com/Afrofanthem",
  linktree: "https://linktr.ee/afrofanthem",
  appleMusic: "https://music.apple.com/us/artist/afro-franthem/1875754660",
  buyMeACoffee: "https://buymeacoffee.com/afrofanthem"
};

export default function ThankYouPage() {
  const location = useLocation();
  const { prizeEligible, club, respondentId } = location.state || {};
  const [referralData, setReferralData] = useState(null);

  useEffect(() => {
    if (respondentId) {
      fetchReferralData();
    }
  }, [respondentId]);

  const fetchReferralData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/referral-stats/${respondentId}`);
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch {
      // referral data not available
    }
  };

  const copyReferralLink = async () => {
    if (referralData?.referral_link) {
      try {
        await navigator.clipboard.writeText(referralData.referral_link);
        toast.success("Referral link copied!");
      } catch (error) {
        toast.error("Failed to copy link");
      }
    }
  };

  const handleShare = async () => {
    const shareText = referralData?.referral_link 
      ? `I just added my voice to the African football fan movement! Join me and we'll both get extra prize draw entries: ${referralData.referral_link}`
      : "I just added my voice to the African football fan movement! Join me in the conversation with Premier League clubs.";
    
    const shareData = {
      title: "Afro Fanthem - African Fan Voice",
      text: shareText,
      url: referralData?.referral_link || "https://www.afrofanthem.com"
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success("Link copied to clipboard!");
      }
    } catch {
      toast.error("Sharing not supported on this device");
    }
  };

  const shareWhatsApp = () => {
    const text = referralData?.referral_link 
      ? `I just made my voice heard for African football fans! Join the movement and we'll both earn extra prize draw entries: ${referralData.referral_link}`
      : "I just made my voice heard for African football fans! Join the movement at https://www.afrofanthem.com";
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Message */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#10B981]" />
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4" data-testid="thankyou-headline">
              Thank You!
            </h1>
            <p className="text-xl text-gray-300 mb-2">
              Your voice has been added to the movement.
            </p>
            <p className="text-gray-400">
              Together, we're building a credible case for fan representation and inclusion.
            </p>
          </div>

          {/* Prize Eligibility */}
          {prizeEligible && (
            <div className="bg-[#FBBF24]/10 border border-[#FBBF24]/30 p-6 rounded-sm mb-8 text-center">
              <Trophy className="w-12 h-12 text-[#FBBF24] mx-auto mb-4" />
              <h2 className="font-heading text-2xl text-[#FBBF24] uppercase mb-2">
                You're in the Prize Draw!
              </h2>
              <p className="text-gray-300">
                You've been entered to win exclusive {club || "club"} merchandise. 
                100 winners will be selected at the end of August. Good luck!
              </p>
            </div>
          )}

          {/* Referral Section */}
          {referralData?.referral_code && (
            <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-6 rounded-sm mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-[#10B981]" />
                <h3 className="font-heading text-xl text-white uppercase">Earn Extra Entries</h3>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Share your unique link with friends. When they complete the survey with a phone number, 
                you both get an extra prize draw entry!
              </p>
              <div className="flex items-center gap-2 bg-black/30 p-3 border border-white/10">
                <input 
                  type="text" 
                  readOnly 
                  value={referralData.referral_link}
                  className="flex-1 bg-transparent text-white text-sm outline-none truncate"
                />
                <button
                  onClick={copyReferralLink}
                  className="flex items-center gap-2 text-[#10B981] hover:text-[#10B981]/80 transition-colors px-3 py-1"
                  data-testid="copy-referral-button"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
              {referralData.total_referrals > 0 && (
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <Gift className="w-4 h-4 text-[#FBBF24]" />
                  <span className="text-gray-400">
                    You've referred <span className="text-[#FBBF24] font-bold">{referralData.total_referrals}</span> {referralData.total_referrals === 1 ? 'friend' : 'friends'}
                    {referralData.bonus_entries > 0 && (
                      <> and earned <span className="text-[#10B981] font-bold">{referralData.bonus_entries}</span> bonus {referralData.bonus_entries === 1 ? 'entry' : 'entries'}!</>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Support Section */}
          <div className="bg-[#141414] border border-white/10 p-6 sm:p-8 rounded-sm mb-8">
            <div className="text-center mb-8">
              <Heart className="w-8 h-8 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl text-white uppercase mb-2">
                Power the Movement
              </h2>
              <p className="text-gray-400 text-sm">
                By following, subscribing, streaming, and donating, you directly support 
                the project financially and help grow the movement. Every action counts.
              </p>
            </div>

            {/* Social Links Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Instagram */}
              <a 
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-pink-500/50 hover:bg-pink-500/5 transition-all flex items-center gap-3"
                data-testid="thankyou-instagram"
              >
                <Instagram className="w-6 h-6 text-pink-500" />
                <span className="text-white font-medium text-sm">Instagram</span>
              </a>

              {/* YouTube */}
              <a 
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-red-500/50 hover:bg-red-500/5 transition-all flex items-center gap-3"
                data-testid="thankyou-youtube"
              >
                <Youtube className="w-6 h-6 text-red-500" />
                <span className="text-white font-medium text-sm">YouTube</span>
              </a>

              {/* Spotify */}
              <a 
                href={SOCIAL_LINKS.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-green-500/50 hover:bg-green-500/5 transition-all flex items-center gap-3"
                data-testid="thankyou-spotify"
              >
                <Music className="w-6 h-6 text-green-500" />
                <span className="text-white font-medium text-sm">Spotify</span>
              </a>

              {/* TikTok */}
              <a 
                href={SOCIAL_LINKS.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-white/50 hover:bg-white/5 transition-all flex items-center gap-3"
                data-testid="thankyou-tiktok"
              >
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                <span className="text-white font-medium text-sm">TikTok</span>
              </a>

              {/* X (Twitter) */}
              <a 
                href={SOCIAL_LINKS.x}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-white/50 hover:bg-white/5 transition-all flex items-center gap-3"
                data-testid="thankyou-x"
              >
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="text-white font-medium text-sm">X</span>
              </a>

              {/* Apple Music */}
              <a 
                href={SOCIAL_LINKS.appleMusic}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-pink-400/50 hover:bg-pink-400/5 transition-all flex items-center gap-3"
                data-testid="thankyou-apple-music"
              >
                <Music className="w-6 h-6 text-pink-400" />
                <span className="text-white font-medium text-sm">Apple Music</span>
              </a>

              {/* Linktree */}
              <a 
                href={SOCIAL_LINKS.linktree}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-[#43E55E]/50 hover:bg-[#43E55E]/5 transition-all flex items-center gap-3"
                data-testid="thankyou-linktree"
              >
                <ExternalLink className="w-6 h-6 text-[#43E55E]" />
                <span className="text-white font-medium text-sm">Linktree</span>
              </a>

              {/* Buy Me a Coffee */}
              <a 
                href={SOCIAL_LINKS.buyMeACoffee}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black/30 border border-white/10 p-4 hover:border-[#FBBF24]/50 hover:bg-[#FBBF24]/5 transition-all flex items-center gap-3"
                data-testid="thankyou-donate"
              >
                <Coffee className="w-6 h-6 text-[#FBBF24]" />
                <span className="text-white font-medium text-sm">Donate</span>
              </a>
            </div>
          </div>

          {/* Share Section */}
          <div className="text-center space-y-3">
            <p className="text-gray-400 mb-4">Help us spread the word!</p>
            <button
              onClick={shareWhatsApp}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#25D366] text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#20BA5A] transition-all"
              data-testid="whatsapp-share-button"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Share on WhatsApp
            </button>
            <button
              onClick={handleShare}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-white/20 transition-colors"
              data-testid="share-button"
            >
              <Share2 className="w-5 h-5" />
              Share via Other
            </button>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-12">
            <Link 
              to="/"
              className="text-gray-400 hover:text-white transition-colors underline"
              data-testid="back-home-link"
            >
              Return to Homepage
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
